/**
 * React bindings for MatchController.
 *
 * The controller is the source of truth and lives outside React; this hook
 * subscribes to it with useSyncExternalStore so a bot action taken from a timer
 * re-renders the battlefield exactly once.
 *
 * Two details matter and are easy to get wrong:
 *
 * 1. The `subscribe` function must be referentially stable. An inline arrow
 *    makes React tear down and re-establish the subscription on every render,
 *    and since subscribing pushes the current snapshot, that is an infinite
 *    render loop.
 * 2. `getSnapshot` must return the same object until something actually
 *    changed, which is why the snapshot is cached in a ref rather than rebuilt
 *    per call.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { MatchController, type MatchConfig, type MatchSnapshot } from './matchController';
import type { BoardController, BoardSnapshot } from './boardController';

/**
 * Create a controller for this config and keep it alive for the life of the
 * component. The config is captured once on purpose: changing a seed or a deck
 * mid-match would mean a different match, so the caller remounts instead.
 */
export function useMatchController(config: MatchConfig): MatchController {
  const [controller] = useState(() => new MatchController(config));

  useEffect(() => {
    void controller.start();
    return () => controller.dispose();
  }, [controller]);

  return controller;
}

/**
 * Subscribe to any board controller. `MatchController` and the Test Lab's
 * `LabController` both satisfy `BoardController`, and the board does not care
 * which one it has.
 */
export function useBoardSnapshot(controller: BoardController): BoardSnapshot {
  const cached = useRef<BoardSnapshot>(controller.snapshot());

  const subscribe = useCallback(
    (onChange: () => void) =>
      controller.subscribe((snapshot) => {
        cached.current = snapshot;
        onChange();
      }),
    [controller],
  );

  const getSnapshot = useCallback(() => cached.current, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useMatchSnapshot(controller: MatchController): MatchSnapshot {
  const cached = useRef<MatchSnapshot>(controller.snapshot());

  const subscribe = useCallback(
    (onChange: () => void) =>
      controller.subscribe((snapshot) => {
        cached.current = snapshot;
        onChange();
      }),
    [controller],
  );

  const getSnapshot = useCallback(() => cached.current, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
